import {
  Box,
  Card,
  CardBody,
  Flex,
  Icon,
  Link,
  Progress,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import { ExternalIngestionFolderFragment } from '@client/graphql/__generated__/types';
import dayjs from 'dayjs';
import { CheckIcon, TriangleAlertIcon } from 'lucide-react';
import { FC } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useShowBlurOnScrollable } from '~/apps/consumer/hooks/useShowBlurOnScrollable';
import { formatKeyTermValue } from '~/common/pages/Compliance/utils/ingestionDocuments.utils';
import {
  DEFAULT_DATETIME_FORMAT,
  formatUnderscoreField,
} from '~/common/utils/formatter';
import { extractNamesFromMetadata } from '../../../../common/pages/Compliance/utils/ingestionFolder';

interface IngestionFoldersTableProps {
  folders: ExternalIngestionFolderFragment[];
  loading: boolean;
  fetchTriggerRef: (ref: HTMLDivElement | null) => void;
  setInitialBlurRef?: (ref: HTMLDivElement | null) => void;
  handleBlurOnScroll?: (event: React.UIEvent<HTMLDivElement>) => void;
}

export const IngestionFoldersTable: FC<IngestionFoldersTableProps> = ({
  folders,
  loading,
  fetchTriggerRef,
  setInitialBlurRef,
  handleBlurOnScroll,
}) => {
  return (
    <Flex direction="column" h="100%" minH="0">
      <Progress
        isIndeterminate
        height="2px"
        size="xs"
        visibility={loading ? 'visible' : 'hidden'}
        width="100%"
      />
      <Flex
        ref={setInitialBlurRef}
        direction="column"
        flex="1"
        minH="0"
        overflow="auto"
        position="relative"
        onScroll={handleBlurOnScroll}
      >
        <Table size="md" variant="simple">
          <Thead
            bgColor="opaque.indigo_800_whiteAlpha_100"
            position="sticky"
            top={0}
            zIndex={1}
          >
            <Tr>
              <Th>Folder Name</Th>
              <Th>Side</Th>
              <Th>Agent</Th>
              <Th>Client</Th>
              <Th>Status</Th>
              <Th>State</Th>
              <Th minW="128px">Issues</Th>
              <Th>Purchase Price</Th>
              <Th>Close Date</Th>
              <Th>Updated At</Th>
              {/* <Th>Due Diligence Date</Th> */}
            </Tr>
          </Thead>
          <Tbody>
            {folders.map((folder) => {
              const { agentName, clientName } = extractNamesFromMetadata(
                folder.metadata,
                folder.transactionSide
              );

              const normalizedFieldValues = folder.normalizedFieldValues || {};
              const purchasePrice = formatKeyTermValue(
                normalizedFieldValues.purchase_price,
                'purchase_price'
              );
              const closeDate = formatKeyTermValue(
                normalizedFieldValues.close_date,
                'close_date'
              );
              // const dueDiligenceDate = formatKeyTermValue(
              //   normalizedFieldValues.due_diligence_end_date,
              //   'due_diligence_end_date'
              // );

              const errorCount = folder.validationErrorCount || 0;

              return (
                <Link
                  key={folder.id}
                  as={RouterLink}
                  cursor="pointer"
                  display="contents"
                  to={`/organizations/compliance/${folder.id}`}
                >
                  <Tr _hover={{ bg: 'whiteAlpha.100' }} transition="all 0.2s">
                    <Td fontWeight="medium">{folder.folderName}</Td>
                    <Td>{folder.transactionSide}</Td>
                    <Td>{agentName}</Td>
                    <Td>{clientName}</Td>
                    <Td>{formatUnderscoreField(folder.status)}</Td>
                    <Td>
                      {folder.transactionPipeline?.usState
                        ? folder.transactionPipeline.usState
                        : '-'}
                    </Td>
                    <Td>
                      <Flex align="center" gap={1}>
                        {errorCount > 0 ? (
                          <>
                            <Icon
                              as={TriangleAlertIcon}
                              color="red.500"
                              flexShrink={0}
                            />
                            <Text>{errorCount} Issues</Text>
                          </>
                        ) : (
                          <>
                            <Icon
                              as={CheckIcon}
                              color="green.500"
                              flexShrink={0}
                            />
                            <Text>No Issues</Text>
                          </>
                        )}
                      </Flex>
                    </Td>
                    <Td>{purchasePrice}</Td>
                    <Td>{closeDate}</Td>
                    <Td>
                      {folder.folderUpdatedAt
                        ? dayjs(folder.folderUpdatedAt as string).format(
                            DEFAULT_DATETIME_FORMAT
                          )
                        : '-'}
                    </Td>
                    {/* <Td>{dueDiligenceDate}</Td> */}
                  </Tr>
                </Link>
              );
            })}
            <Tr>
              <Td border="none" colSpan={11} p={0}>
                <Box ref={fetchTriggerRef} height="1px" width="100%" />
              </Td>
            </Tr>
          </Tbody>
        </Table>
      </Flex>
    </Flex>
  );
};

interface IngestionFoldersTableCardProps {
  folders: ExternalIngestionFolderFragment[];
  loading: boolean;
  fetchTriggerRef: (ref: HTMLDivElement | null) => void;
  hasActiveFilters: boolean;
}

export const IngestionFoldersTableCard: FC<IngestionFoldersTableCardProps> = ({
  folders,
  loading,
  fetchTriggerRef,
  hasActiveFilters,
}) => {
  const { showBlur, setInitialBlurRef, handleBlurOnScroll } =
    useShowBlurOnScrollable();

  return (
    <Card
      _after={{
        display: showBlur ? 'block' : 'none',
        content: '""',
        background:
          'linear-gradient(0deg, #111527 0%, rgba(17, 21, 39, 0.00) 100%)',
        width: '100%',
        height: '50px',
        position: 'absolute',
        bottom: 0,
        left: 0,
        pointerEvents: 'none',
        zIndex: 2,
      }}
      display="flex"
      flex="1"
      flexDirection="column"
      minH="0"
      overflow="hidden"
      position="relative"
    >
      <CardBody display="flex" flex="1" flexDirection="column" minH="0" p={0}>
        {folders.length === 0 && !loading ? (
          <Box p={6}>
            <Text color="gray.500">
              {hasActiveFilters
                ? 'No ingestion folders match the current filters. Try adjusting your search or filters.'
                : 'No ingestion folders found for this organization.'}
            </Text>
          </Box>
        ) : (
          <IngestionFoldersTable
            fetchTriggerRef={fetchTriggerRef}
            folders={folders}
            handleBlurOnScroll={handleBlurOnScroll}
            loading={loading}
            setInitialBlurRef={setInitialBlurRef}
          />
        )}
      </CardBody>
    </Card>
  );
};
